import TurndownService from "turndown";
import crypto from "crypto";

export function htmlWordCounter(content) {
  if (content) {
    const parser = new DOMParser();
    const dom = parser.parseFromString(content, "text/html");
    const text = dom.body.textContent;
    return text.split(/\s+/).filter(function (word) {
      return word.length > 0;
    }).length;
  }
  return 0;
}

export function convertTimetoTimeAgo(timestamp) {
  if (!timestamp) return "never";

  if (timestamp[timestamp.length - 1] !== "Z") {
    timestamp = timestamp + "Z";
  }
  const parsedDatetime = new Date(timestamp);
  const currentDatetime = new Date();

  const timeDifference = currentDatetime - parsedDatetime;

  let timeAgo = "";
  if (timeDifference > 365 * 24 * 60 * 60 * 1000) {
    timeAgo = `${Math.floor(timeDifference / (365 * 24 * 60 * 60 * 1000))} years ago`;
  } else if (timeDifference > 24 * 60 * 60 * 1000) {
    timeAgo = `${Math.floor(timeDifference / (24 * 60 * 60 * 1000))} days ago`;
  } else if (timeDifference > 60 * 60 * 1000) {
    timeAgo = `${Math.floor(timeDifference / (60 * 60 * 1000))} hours ago`;
  } else if (timeDifference > 60 * 1000) {
    timeAgo = `${Math.floor(timeDifference / (60 * 1000))} minutes ago`;
  } else if (timeDifference > 1000) {
    const seconds = Math.ceil(timeDifference / 1000);
    timeAgo = `${seconds} seconds ago`;
  } else {
    timeAgo = "just now";
  }

  return timeAgo;
}

export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function humanifyDate(timestamp) {
  // Convert date to Month Day, Year
  const parsedDatetime = new Date(timestamp);
  const year = parsedDatetime.getFullYear();
  const month = parsedDatetime.toLocaleString("default", { month: "long" });
  const day = parsedDatetime.getDate();
  const formattedDate = `${month} ${day}, ${year}`;

  return formattedDate;
}

export function debounce(func, wait) {
  let timeout;

  function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  }

  // Backwards-compatible enhancement: allow callers to cancel a pending debounce.
  executedFunction.cancel = () => {
    clearTimeout(timeout);
  };

  return executedFunction;
}

export function convertHtmlToMarkdown(html) {
  const turndownService = new TurndownService();
  return turndownService.turndown(html);
}

/**
 * Apply a backend agent event (or local optimistic action) to the chat state.
 *
 * Each conversation holds `chats: Row[]` — a flat, chronological list of
 * normalized rows that mirror the backend's `agent_messages` tree (one entry
 * per node along the active chain). Row shape:
 *
 *   {
 *     kind: "user" | "assistant" | "tool_result" | "compaction",
 *     message_id: number,         // negative for optimistic / pending rows
 *     parent_id: number | null,
 *     content: string | null,     // accumulated by message_update_text
 *     tool_calls?: ToolCall[],    // assistant rows
 *     token_usage?: TokenUsage,   // assistant rows (latest = running total)
 *     tool_call_id?: string,      // tool_result rows (links to its assistant)
 *     tool_name?: string,
 *     tool_status?: "ok" | "error",
 *     tool_duration_ms?: number,
 *     compaction_first_kept_id?: number,
 *     compaction_tokens_before?: number,
 *     streaming?: boolean,        // true between message_start and message_end
 *     optimistic?: boolean,       // true if added before the WS round-trip
 *   }
 *
 * Ephemeral state lives at the conversation level:
 *   thinkingTokens: { text, kind?, tool? }[]   (cleared on agent_end / new turn)
 *   hasError, errorMessage, canRetry
 *   progressMessage   (server-side `type: "progress"` events during a turn)
 *
 * Action types:
 *   optimistic_user           { tempId, content }
 *   event_message_start       { message_id, kind, parent_id }
 *   event_message_update_text { message_id, text }
 *   event_message_update_tool_call { message_id, index, arguments_delta }
 *   event_message_end         { message_id, content?, tool_calls?, token_usage? }
 *   event_tool_end            { tool_call_id, result_message_id, status, duration_ms }
 *   event_compaction          { message_id, summary, tokens_before, first_kept_id }
 *   event_thinking            { text, kind?, tool? }
 *   event_clear_thinking      {}
 *   event_progress            { message }
 *   event_error               { message, retryable? }
 *   event_agent_end           { leaf_message_id, total_tokens }
 */
export function updateConversationChats(prevConversations, activeConversation, payload, updateType) {
  return prevConversations.map((conversation) => {
    if (conversation.conversation_id !== activeConversation) return conversation;

    const updatedAt = new Date().toISOString();
    const chats = conversation.chats || [];

    const replaceById = (id, mutate) => {
      const idx = chats.findIndex((m) => m.message_id === id);
      if (idx < 0) return chats;
      return [...chats.slice(0, idx), mutate(chats[idx]), ...chats.slice(idx + 1)];
    };

    switch (updateType) {
      case "optimistic_user": {
        // Insert BOTH a user row and an empty assistant placeholder so the
        // loader can render the moment the user clicks send — without waiting
        // for the WS handshake, bootstrap, and the first real message_start
        // (which together can be 300-800ms). Both rows are replaced in place
        // when the real message_start events arrive.
        return {
          ...conversation,
          updated_at: updatedAt,
          chats: [
            ...chats,
            {
              kind: "user",
              message_id: payload.tempId,
              parent_id: null,
              content: payload.content,
              streaming: false,
              optimistic: true,
            },
            {
              kind: "assistant",
              message_id: payload.tempId - 1,
              parent_id: null,
              content: "",
              streaming: true,
              optimistic: true,
            },
          ],
          message_count: (conversation.message_count || 0) + 2,
        };
      }

      case "event_message_start": {
        // Replace an optimistic placeholder of the same kind in place if one
        // exists; otherwise append. Walking from the tail finds the row we
        // just optimistically inserted on send (or after rewind).
        let optIdx = -1;
        for (let i = chats.length - 1; i >= 0; i--) {
          const r = chats[i];
          if (r.optimistic && r.kind === payload.kind) {
            optIdx = i;
            break;
          }
        }

        const newRow = {
          kind: payload.kind,
          message_id: payload.message_id,
          parent_id: payload.parent_id,
          content: "",
          streaming: true,
        };

        const nextChats =
          optIdx >= 0
            ? [...chats.slice(0, optIdx), newRow, ...chats.slice(optIdx + 1)]
            : [...chats, newRow];

        return {
          ...conversation,
          updated_at: updatedAt,
          // Starting a new assistant turn clears the ephemeral thinking buffer.
          thinkingTokens: payload.kind === "assistant" ? [] : conversation.thinkingTokens,
          chats: nextChats,
          message_count:
            optIdx >= 0
              ? conversation.message_count
              : (conversation.message_count || 0) + 1,
        };
      }

      case "event_message_update_text": {
        return {
          ...conversation,
          chats: replaceById(payload.message_id, (row) => ({
            ...row,
            content: (row.content || "") + (payload.text || ""),
          })),
        };
      }

      case "event_message_update_tool_call": {
        // Accumulate streamed edit_chapter arguments per tool-call index.
        // ChatList derives ghost cards from these; they stop rendering once
        // the real staged cards (or a validation failure) arrive, and never
        // survive a reload (rows rebuilt from the DB carry no partials).
        return {
          ...conversation,
          chats: replaceById(payload.message_id, (row) => ({
            ...row,
            tool_call_partials: {
              ...(row.tool_call_partials || {}),
              [payload.index]:
                ((row.tool_call_partials || {})[payload.index] || "") + (payload.arguments_delta || ""),
            },
          })),
        };
      }

      case "event_message_end": {
        return {
          ...conversation,
          updated_at: updatedAt,
          chats: replaceById(payload.message_id, (row) => ({
            ...row,
            // For tool_result rows the orchestrator always sends content; for
            // assistant rows it sends the final aggregated text. Either way we
            // overwrite — the streaming deltas already populated `content`,
            // and the end frame is authoritative.
            content: payload.content !== undefined ? payload.content : row.content,
            tool_calls: payload.tool_calls ?? row.tool_calls,
            token_usage: payload.token_usage ?? row.token_usage,
            streaming: false,
          })),
        };
      }

      case "event_tool_end": {
        return {
          ...conversation,
          chats: replaceById(payload.result_message_id, (row) => ({
            ...row,
            tool_call_id: payload.tool_call_id,
            tool_status: payload.status,
            tool_duration_ms: payload.duration_ms,
          })),
        };
      }

      case "event_compaction": {
        return {
          ...conversation,
          chats: [
            ...chats,
            {
              kind: "compaction",
              message_id: payload.message_id,
              parent_id: null,
              content: payload.summary || "",
              compaction_first_kept_id: payload.first_kept_id,
              compaction_tokens_before: payload.tokens_before,
            },
          ],
          message_count: (conversation.message_count || 0) + 1,
        };
      }

      case "event_thinking": {
        // Merge consecutive plain reasoning tokens (no `kind`) into one line so
        // the stream doesn't flood the UI; tool/refresh tokens always break.
        const existing = conversation.thinkingTokens || [];
        const incoming = { text: payload.text, kind: payload.kind, tool: payload.tool };
        const prev = existing[existing.length - 1];
        let merged;
        if (prev && !prev.kind && !incoming.kind) {
          const sep = prev.text.endsWith(" ") || incoming.text.startsWith(" ") ? "" : " ";
          merged = [...existing.slice(0, -1), { ...prev, text: prev.text + sep + incoming.text }];
        } else {
          merged = [...existing, incoming];
        }
        return { ...conversation, thinkingTokens: merged.slice(-10) };
      }

      case "event_clear_thinking":
        return { ...conversation, thinkingTokens: [] };

      case "event_progress":
        return { ...conversation, progressMessage: payload.message };

      case "event_error":
        return {
          ...conversation,
          hasError: true,
          errorMessage: payload.message,
          canRetry: payload.retryable !== false,
        };

      case "event_agent_start":
        // Marks the start of a live agent turn. Pairs with `event_agent_end`
        // to bound an "in flight" window — the orphan-tool synthesis in
        // ChatList only fires outside this window so tools mid-execution
        // don't render as error chips while waiting for their result row.
        return { ...conversation, turnInFlight: true };

      case "event_agent_end":
        return {
          ...conversation,
          leaf_message_id: payload.leaf_message_id,
          total_tokens: payload.total_tokens,
          thinkingTokens: [],
          progressMessage: undefined,
          turnInFlight: false,
        };

      case "optimistic_rewind": {
        // Mirror the server-side rewind: drop every row from `message_id`
        // onward (inclusive). This runs BEFORE the WSS round-trip so the UI
        // doesn't have a flicker where the old turn coexists with the new.
        const idx = chats.findIndex((m) => m.message_id === payload.message_id);
        if (idx < 0) return conversation;
        return {
          ...conversation,
          updated_at: updatedAt,
          chats: chats.slice(0, idx),
          // Clear ephemeral state from the previous turn — the next message
          // events will repopulate them.
          thinkingTokens: [],
          hasError: false,
          errorMessage: undefined,
          canRetry: undefined,
          progressMessage: undefined,
        };
      }

      case "event_stream_terminated": {
        // WS dropped or closed before agent_end. Sweep any row still in
        // mid-stream so the UI doesn't show stuck spinners forever:
        //   - assistant/tool_result rows with streaming=true → streaming=false
        //   - tool_result rows that never got their tool_end → mark error
        //     so the chip stops rendering as "running…" / silent ok.
        // tool_calls without a matching tool_result row at all are handled
        // at render time in ChatList (see `groupSettled` orphan synthesis)
        // — that path covers historical loads too.
        const swept = chats.map((row) => {
          let next = row;
          if (row.kind === "tool_result" && !row.tool_status) {
            next = { ...next, tool_status: "error" };
          }
          if (next.streaming) {
            next = { ...next, streaming: false };
          }
          return next;
        });
        return {
          ...conversation,
          chats: swept,
          thinkingTokens: [],
          progressMessage: undefined,
          turnInFlight: false,
        };
      }

      case "error_flag":
        // Non-stream error pinned on the conversation (e.g. daily-token-cap
        // preflight). Same shape as `event_error` but with an explicit
        // `canRetry` flag from the caller — the cap is non-retryable.
        return {
          ...conversation,
          hasError: true,
          errorMessage: payload.message,
          canRetry: payload.canRetry !== undefined ? payload.canRetry : false,
          thinkingTokens: [],
          progressMessage: undefined,
        };

      default:
        return conversation;
    }
  });
}

export function isJSON(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

const secretSalt = process.env.NEXT_PUBLIC_ENCRYPTION_SECRET_SALT;
const secretKey = secretSalt.slice(0, 32);
const iv = Buffer.alloc(16, 0);

export function encodeBookId(bookId) {
  const cipher = crypto.createCipheriv("aes-256-cbc", secretKey, iv);
  let encrypted = cipher.update(bookId.toString(), "utf8", "hex");
  encrypted += cipher.final("hex");
  return encrypted;
}

export function decodeBookId(encodedId) {
  if (encodedId === "0") {
    return "0";
  }
  try {
    const decipher = crypto.createDecipheriv("aes-256-cbc", secretKey, iv);
    let decrypted = decipher.update(encodedId, "hex", "utf8");
    decrypted += decipher.final("utf8");
    return decrypted;
  } catch (error) {
    console.error("Invalid encodedId: ", error);
    return -99;
  }
}

export function cleanHtml(html) {
  const cleanContent = html
    .replace(/<br\s*\/?>/gi, "\n") // Convert <br> tags to newlines
    .replace(/<p[^>]*>/gi, "") // Remove opening <p> tags
    .replace(/<\/p>/gi, "\n\n") // Convert closing </p> tags to double newlines
    .replace(/<div[^>]*>/gi, "") // Remove opening <div> tags
    .replace(/<\/div>/gi, "\n\n") // Convert closing </div> tags to double newlines
    .replace(/<[^>]*>/g, "") // Remove all remaining HTML tags
    .replace(/&nbsp;/g, " ") // Convert &nbsp; to spaces
    .replace(/&amp;/g, "&") // Convert &amp; to &
    .replace(/&lt;/g, "<") // Convert &lt; to <
    .replace(/&gt;/g, ">") // Convert &gt; to >
    .replace(/&quot;/g, '"') // Convert &quot; to "
    .replace(/&#39;/g, "'") // Convert &#39; to '
    .replace(/\n\s*\n\s*\n/g, "\n\n") // Remove excessive blank lines
    .replace(/^\s+|\s+$/g, ""); // Trim whitespace from start and end

  return cleanContent;
}

export function sanitizeFileName(fileName) {
  return fileName
    .replace(/[<>:"/\\|?*]/g, "") // Remove invalid chars
    .substring(0, 255); // Limit length
}

export function normalizeTimestamp(timestamp) {
  if (!timestamp) return timestamp;

  // If timestamp doesn't end with Z, add it
  if (timestamp[timestamp.length - 1] !== "Z") {
    return timestamp + "Z";
  }
  return timestamp;
}

// The service's own error text, or `fallback`.
export const errMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};
